import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardLink } from '../../components/CardLink';
import { apiClient } from '../../packages/api-client';
import { formatDueDate } from '../../utils/date';
import { usePageTitle } from '../../hooks/usePageTitle';
import { PageHeader, StateView } from '../../packages/ui';
import { X, ArrowRight } from 'lucide-react';
import { PriorityIcon, StatusIcon, EntityIcon } from '../../components/taskIcons';
import { priorityColor, hexAlpha, priorityTint, onHex } from '../../utils/statusColors';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TimelineTask {
    id: string;
    title: string;
    status: string;
    priority: string;
    due_date?: string;
}

interface ProjectTimeline {
    id: string;
    name: string;
    color: string;
    icon: string;
    status: string;
    task_count: number;
    start_date?: string;
    end_date?: string;
    tasks: TimelineTask[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseDate(s?: string): Date | null {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
}

function toISO(d: Date) { return d.toISOString().split('T')[0]; }
function addDays(d: Date, n: number) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function diffDays(a: Date, b: Date) { return Math.round((b.getTime() - a.getTime()) / 86400000); }

function formatLabel(d: Date, interval: 'day' | 'week' | 'month') {
    if (interval === 'month') return d.toLocaleDateString('en', { month: 'short', year: '2-digit' });
    if (interval === 'week') return d.toLocaleDateString('en', { month: 'short', day: 'numeric' });
    return d.toLocaleDateString('en', { month: 'short', day: 'numeric' });
}

const STATUS_LABELS: Record<string, string> = {
    todo: 'To Do', in_progress: 'In Progress', blocked: 'Blocked', review: 'Review', done: 'Done',
    planning: 'Planning', active: 'Active', on_hold: 'On Hold', completed: 'Completed', archived: 'Archived',
};

// ─── Component ────────────────────────────────────────────────────────────────

export function TimelinePage() {
    usePageTitle('Portfolio timeline');
    const navigate = useNavigate();
    const [projects, setProjects] = useState<ProjectTimeline[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selected, setSelected] = useState<ProjectTimeline | null>(null);
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        apiClient.get<{ data: ProjectTimeline[] }>('/projects/timeline')
            .then(r => { setProjects(r.data.data ?? []); setLoading(false); })
            .catch(() => { setError('The timeline could not be loaded. Check your connection and retry.'); setLoading(false); });
    }, []);
    useEffect(load, [load]);

    // Close panel on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                setSelected(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Calculate the visible date range from all projects
    const { rangeStart, rangeEnd, totalDays } = useMemo(() => {
        const starts: Date[] = [];
        const ends: Date[] = [];
        projects.forEach(p => {
            const s = parseDate(p.start_date);
            const e = parseDate(p.end_date);
            if (s) starts.push(s);
            if (e) ends.push(e);
        });
        const today = new Date();
        const rangeStart = starts.length ? addDays(new Date(Math.min(...starts.map(d => d.getTime()))), -7) : addDays(today, -14);
        const rangeEnd = ends.length ? addDays(new Date(Math.max(...ends.map(d => d.getTime()))), 14) : addDays(today, 60);
        const totalDays = Math.max(diffDays(rangeStart, rangeEnd), 1);
        return { rangeStart, rangeEnd, totalDays };
    }, [projects]);

    // Build tick marks (months if >90 days, weeks if 30-90, days if <30)
    const ticks = useMemo(() => {
        const interval: 'day' | 'week' | 'month' = totalDays > 90 ? 'month' : totalDays > 30 ? 'week' : 'day';
        const result: { label: string; pct: number }[] = [];
        let d = new Date(rangeStart);
        // Advance to next clean boundary
        if (interval === 'month') d = new Date(d.getFullYear(), d.getMonth(), 1);
        else if (interval === 'week') {
            const day = d.getDay();
            d = addDays(d, (7 - day) % 7); // next Monday
        }
        while (d <= rangeEnd) {
            const pct = (diffDays(rangeStart, d) / totalDays) * 100;
            if (pct >= 0 && pct <= 100) {
                result.push({ label: formatLabel(d, interval), pct });
            }
            if (interval === 'month') d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
            else if (interval === 'week') d = addDays(d, 7);
            else d = addDays(d, 1);
        }
        return result;
    }, [rangeStart, rangeEnd, totalDays]);

    // Today marker
    const todayPct = useMemo(() => {
        const t = new Date();
        const pct = (diffDays(rangeStart, t) / totalDays) * 100;
        return pct >= 0 && pct <= 100 ? pct : null;
    }, [rangeStart, totalDays]);

    const ROW_HEIGHT = 48;

    if (loading || error) return (
        <div>
            <PageHeader title="Portfolio timeline" />
            {loading
                ? <StateView state="loading" label="Loading timeline" lines={6} />
                : <StateView state="error" message={error} onRetry={load} />}
        </div>
    );

    return (
        <div style={{ position: 'relative' }}>
            <PageHeader
                title="Portfolio timeline"
                subtitle={<>
                    {projects.length} project{projects.length !== 1 ? 's' : ''}
                    {' · '}
                    {toISO(rangeStart)} → {toISO(rangeEnd)}
                </>}
            />

            {projects.length === 0 ? (
                <StateView state="empty" title="No projects to show" message="Projects you can see appear here with their dates. Create a project, or ask for access to one." />
            ) : (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                    {/* Timeline grid */}
                    <div style={{ display: 'flex', overflow: 'auto' }}>
                        {/* Project name column */}
                        <div style={{
                            minWidth: 220, maxWidth: 220, flexShrink: 0, borderRight: '1px solid var(--color-border)',
                            background: 'var(--color-surface)',
                        }}>
                            {/* Header spacer */}
                            <div style={{ height: 36, borderBottom: '1px solid var(--color-border)', padding: '0 1rem', display: 'flex', alignItems: 'center' }}>
                                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Project</span>
                            </div>
                            {projects.map(p => (
                                <div
                                    key={p.id}
                                    onClick={() => setSelected(p)}
                                    style={{
                                        height: ROW_HEIGHT, display: 'flex', alignItems: 'center', gap: '0.5rem',
                                        padding: '0 1rem', cursor: 'pointer', borderBottom: '1px solid var(--color-border)',
                                        background: hoveredId === p.id ? 'var(--color-surface-2)' : 'transparent',
                                        transition: 'background 0.12s',
                                    }}
                                    onMouseEnter={() => setHoveredId(p.id)}
                                    onMouseLeave={() => setHoveredId(null)}
                                >
                                    <div style={{
                                        width: 28, height: 28, borderRadius: '0.4rem',
                                        background: hexAlpha(p.color, 0.13), color: p.color,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '0.9rem', flexShrink: 0,
                                    }}>
                                        <EntityIcon icon={p.icon} size={14} />
                                    </div>
                                    <div style={{ overflow: 'hidden' }}>
                                        <div style={{
                                            fontWeight: 600, fontSize: 'var(--font-size-sm)',
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                        }}>
                                            {p.name}
                                        </div>
                                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                                            {p.task_count} task{p.task_count !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Gantt chart area */}
                        <div style={{ flex: 1, minWidth: 0, overflow: 'auto', position: 'relative' }}>
                            {/* Tick header */}
                            <div style={{
                                height: 36, borderBottom: '1px solid var(--color-border)',
                                position: 'relative', background: 'var(--color-surface)',
                            }}>
                                {ticks.map((t, i) => (
                                    <div key={i} style={{
                                        position: 'absolute', left: `${t.pct}%`, top: 0,
                                        height: '100%', display: 'flex', alignItems: 'center',
                                        paddingLeft: 4,
                                    }}>
                                        <span style={{
                                            fontSize: '0.6rem', color: 'var(--color-text-muted)',
                                            fontWeight: 500, whiteSpace: 'nowrap',
                                        }}>{t.label}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Project rows */}
                            <div style={{ position: 'relative', minWidth: 600 }}>
                                {/* Tick vertical lines */}
                                {ticks.map((t, i) => (
                                    <div key={i} style={{
                                        position: 'absolute', left: `${t.pct}%`, top: 0, bottom: 0,
                                        width: 1, background: 'var(--color-border)', opacity: 0.5,
                                        pointerEvents: 'none',
                                    }} />
                                ))}

                                {/* Today line */}
                                {todayPct !== null && (
                                    <div style={{
                                        position: 'absolute', left: `${todayPct}%`, top: 0, bottom: 0,
                                        width: 2, background: 'var(--accent)', zIndex: 5, pointerEvents: 'none',
                                    }}>
                                        <div style={{
                                            position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                                            background: 'var(--accent)', color: 'var(--on-accent)', fontSize: '0.55rem',
                                            padding: '1px 5px', borderRadius: 3, whiteSpace: 'nowrap',
                                        }}>Today</div>
                                    </div>
                                )}

                                {/* Gantt bars */}
                                {projects.map(p => {
                                    const start = parseDate(p.start_date);
                                    const end = parseDate(p.end_date);
                                    let leftPct: number, widthPct: number;

                                    if (!start && !end) {
                                        // No tasks at all — show a thin placeholder at today
                                        const todayPos = (diffDays(rangeStart, new Date()) / totalDays) * 100;
                                        leftPct = Math.max(0, todayPos);
                                        widthPct = 0.5;
                                    } else {
                                        const s = start ?? new Date();
                                        const e = end ?? s;
                                        leftPct = Math.max(0, (diffDays(rangeStart, s) / totalDays) * 100);
                                        const rightPct = Math.min(100, (diffDays(rangeStart, e) / totalDays) * 100);
                                        widthPct = Math.max(0.5, rightPct - leftPct);
                                    }

                                    return (
                                        <div
                                            key={p.id}
                                            style={{
                                                height: ROW_HEIGHT, borderBottom: '1px solid var(--color-border)',
                                                position: 'relative', display: 'flex', alignItems: 'center',
                                                background: hoveredId === p.id ? 'var(--color-surface-2)' : 'transparent',
                                                transition: 'background 0.12s',
                                            }}
                                            onMouseEnter={() => setHoveredId(p.id)}
                                            onMouseLeave={() => setHoveredId(null)}
                                        >
                                            {/* Gantt bar */}
                                            <div
                                                onClick={() => setSelected(p)}
                                                title={`${p.name}: ${p.start_date ?? '?'} → ${p.end_date ?? '?'} (${p.task_count} tasks)`}
                                                style={{
                                                    position: 'absolute',
                                                    left: `${leftPct}%`,
                                                    width: `${widthPct}%`,
                                                    height: 24,
                                                    borderRadius: 6,
                                                    background: p.color,
                                                    opacity: hoveredId === p.id ? 1 : 0.85,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    paddingLeft: 8,
                                                    overflow: 'hidden',
                                                    transition: 'opacity 0.15s, transform 0.1s',
                                                    transform: hoveredId === p.id ? 'scaleY(1.1)' : 'scaleY(1)',
                                                    boxShadow: hoveredId === p.id ? `0 2px 12px ${hexAlpha(p.color, 0.33)}` : 'none',
                                                }}
                                            >
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 4,
                                                    color: onHex(p.color), fontSize: '0.65rem', fontWeight: 600,
                                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                                    textShadow: onHex(p.color) === '#FFFFFF' ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
                                                }}>
                                                    <EntityIcon icon={p.icon} size={12} />
                                                    <span>{p.name}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Task flyout panel */}
            {selected && (
                <div style={{
                    position: 'fixed', top: 0, right: 0, bottom: 0, width: 380,
                    background: 'var(--color-surface)', borderLeft: '1px solid var(--color-border)',
                    boxShadow: 'var(--shadow-lg)',
                    display: 'flex', flexDirection: 'column', zIndex: 200,
                    animation: 'slideInRight 0.2s ease',
                }} ref={panelRef}>
                    <style>{`
                        @keyframes slideInRight {
                            from { transform: translateX(100%); opacity: 0; }
                            to   { transform: translateX(0);    opacity: 1; }
                        }
                    `}</style>

                    {/* Panel header */}
                    <div style={{
                        padding: '1.25rem 1.25rem 1rem',
                        borderBottom: '1px solid var(--color-border)',
                        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                            <div style={{
                                width: 40, height: 40, borderRadius: '0.6rem',
                                background: hexAlpha(selected.color, 0.13), color: selected.color,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '1.25rem', flexShrink: 0,
                            }}>
                                <EntityIcon icon={selected.icon} size={16} />
                            </div>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 'var(--font-size-base)' }}>{selected.name}</div>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                                    <span style={{
                                        background: hexAlpha(selected.color, 0.13), color: selected.color,
                                        padding: '1px 7px', borderRadius: 99, fontWeight: 600, textTransform: 'capitalize',
                                    }}>
                                        {STATUS_LABELS[selected.status] ?? selected.status}
                                    </span>
                                    {' · '}
                                    {selected.task_count} task{selected.task_count !== 1 ? 's' : ''}
                                </div>
                            </div>
                        </div>
                        <button onClick={() => setSelected(null)} aria-label="Close panel" style={{
                            background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem',
                            color: 'var(--color-text-muted)', padding: '2px 4px', lineHeight: 1,
                            display: 'inline-flex', alignItems: 'center',
                        }}><X size={18} aria-hidden="true" /></button>
                    </div>

                    {/* Date span */}
                    {(selected.start_date || selected.end_date) && (
                        <div style={{
                            padding: '0.75rem 1.25rem', borderBottom: '1px solid var(--color-border)',
                            display: 'flex', gap: '1.5rem', fontSize: 'var(--font-size-sm)',
                        }}>
                            <div>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Start</div>
                                <div style={{ fontWeight: 600 }}>{selected.start_date ?? '—'}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>End</div>
                                <div style={{ fontWeight: 600 }}>{selected.end_date ?? '—'}</div>
                            </div>
                            {selected.start_date && selected.end_date && (
                                <div>
                                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Duration</div>
                                    <div style={{ fontWeight: 600 }}>
                                        {diffDays(parseDate(selected.start_date)!, parseDate(selected.end_date)!) + 1} days
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Task list */}
                    <div style={{ flex: 1, overflow: 'auto', padding: '0.75rem 1.25rem' }}>
                        <div style={{ fontWeight: 700, marginBottom: '0.75rem', fontSize: 'var(--font-size-sm)' }}>
                            Tasks ({selected.tasks.length})
                        </div>
                        {selected.tasks.length === 0 ? (
                            <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', textAlign: 'center', padding: '1.5rem 0' }}>
                                No tasks in this project yet.
                            </p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                {selected.tasks.map(t => (
                                    <CardLink
                                        key={t.id}
                                        to={`/tasks/${t.id}`}
                                        ariaLabel={t.title}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '0.65rem',
                                            padding: '0.5rem 0.75rem', borderRadius: 'var(--radius-md)',
                                            background: 'var(--color-surface-2)', cursor: 'pointer',
                                            fontSize: 'var(--font-size-sm)', transition: 'background 0.12s',
                                        }}
                                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-surface-3, var(--color-border))')}
                                        onMouseLeave={e => (e.currentTarget.style.background = 'var(--color-surface-2)')}
                                    >
                                        {/* Status icon */}
                                        <StatusIcon status={t.status} size={12} />
                                        {/* Title */}
                                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {t.title}
                                        </span>
                                        {/* Priority badge */}
                                        <span style={{
                                            fontSize: '0.65rem',
                                            background: priorityTint(t.priority, 0.13),
                                            color: priorityColor(t.priority),
                                            padding: '1px 6px', borderRadius: 99, textTransform: 'capitalize', flexShrink: 0,
                                            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                                        }}>
                                            <PriorityIcon priority={t.priority} /> {t.priority}
                                        </span>
                                        {/* Due date */}
                                        {t.due_date && (
                                            <span style={{ fontSize: '0.65rem', color: 'var(--color-text-muted)', flexShrink: 0 }}>
                                                {formatDueDate(t.due_date, { month: 'short', day: 'numeric' })}
                                            </span>
                                        )}
                                    </CardLink>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Action buttons */}
                    <div style={{
                        padding: '1rem 1.25rem', borderTop: '1px solid var(--color-border)',
                        display: 'flex', gap: '0.5rem',
                    }}>
                        <button className="btn btn-primary btn-md" style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
                            onClick={() => navigate(`/projects/${selected.id}`)}>
                            Open Project <ArrowRight size={16} aria-hidden="true" />
                        </button>
                        <button className="btn btn-ghost btn-md"
                            onClick={() => navigate(`/projects/${selected.id}/board`)}>
                            Board
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
